import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Banknote, Plus, CheckCircle, Clock, XCircle, Loader2, Settings2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoansPage = () => {
  const { user, isHR } = useAuth();
  const [loans, setLoans] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [editConfig, setEditConfig] = useState(null);
  const [form, setForm] = useState({ amount: '', reason: '', repayment_months: 9 });
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [loansRes, configRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/loans`, { withCredentials: true }),
        isHR ? axios.get(`${API_URL}/api/loans/config`, { withCredentials: true }) : Promise.resolve({ data: null }),
      ]);
      if (loansRes.status === 'fulfilled') setLoans(loansRes.value.data);
      if (configRes.status === 'fulfilled' && configRes.value.data) setConfig(configRes.value.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [isHR]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApply = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter valid amount'); return; }
    if (!form.reason.trim()) { toast.error('Reason is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/loans/apply`, {
        amount: parseFloat(form.amount), reason: form.reason, repayment_months: parseInt(form.repayment_months) || 9,
      }, { withCredentials: true });
      toast.success('Loan application submitted!');
      setApplyOpen(false);
      setForm({ amount: '', reason: '', repayment_months: 9 });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleApprove = async (loanId, status) => {
    try {
      await axios.put(`${API_URL}/api/loans/${loanId}/approve`, { status }, { withCredentials: true });
      toast.success(`Loan ${status}`);
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.put(`${API_URL}/api/loans/config`, editConfig, { withCredentials: true });
      toast.success('Loan config saved');
      setConfigOpen(false);
      fetchData();
    } catch (err) { toast.error('Failed to save'); }
  };

  const formatCurrency = (v) => `INR ${(v || 0).toLocaleString('en-IN')}`;

  const getStatusBadge = (s) => {
    const cfg = { pending: { cls: 'bg-amber-100 text-amber-700', icon: Clock }, approved: { cls: 'badge-success', icon: CheckCircle }, rejected: { cls: 'bg-red-100 text-red-700', icon: XCircle } };
    const c = cfg[s] || cfg.pending;
    return <Badge className={`${c.cls} gap-1`}><c.icon className="w-3 h-3" /> {s}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="loans-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Banknote className="w-7 h-7 text-green-600" /> Loans & Advances</h1>
          <p className="text-muted-foreground">Apply for salary advances with automated EMI deductions</p>
        </div>
        <div className="flex gap-2">
          <Button className="gap-2" onClick={() => setApplyOpen(true)} data-testid="apply-loan-btn"><Plus className="w-4 h-4" /> Apply for Loan</Button>
          {isHR && <Button variant="outline" className="gap-2" onClick={() => { setEditConfig(config ? { ...config } : {}); setConfigOpen(true); }} data-testid="loan-config-btn"><Settings2 className="w-4 h-4" /> Configure</Button>}
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Loan Applications</CardTitle><CardDescription>{loans.length} application(s)</CardDescription></CardHeader>
        <CardContent>
          {loans.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr>{isHR && <th>Employee</th>}<th>Amount</th><th>EMI</th><th>Repay Months</th><th>Interest</th><th>Reason</th><th>Status</th><th>Applied</th>{isHR && <th>Actions</th>}</tr></thead>
                <tbody>
                  {loans.map(l => (
                    <tr key={l.id}>
                      {isHR && <td className="font-medium text-sm">{l.employee_name}</td>}
                      <td className="font-mono">{formatCurrency(l.amount)}</td>
                      <td className="font-mono text-sm">{formatCurrency(l.emi)}/mo</td>
                      <td className="text-center">{l.repayment_months}</td>
                      <td className="text-center">{l.interest_rate}%</td>
                      <td className="text-sm max-w-xs truncate">{l.reason}</td>
                      <td>{getStatusBadge(l.status)}</td>
                      <td className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString('en-IN')}</td>
                      {isHR && <td>
                        {l.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="text-emerald-600 h-7 text-xs" onClick={() => handleApprove(l.id, 'approved')}>Approve</Button>
                            <Button size="sm" variant="outline" className="text-red-600 h-7 text-xs" onClick={() => handleApprove(l.id, 'rejected')}>Reject</Button>
                          </div>
                        )}
                      </td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-center text-muted-foreground py-8">No loan applications yet.</p>}
        </CardContent>
      </Card>

      {/* Apply Dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Apply for Loan / Advance</DialogTitle><DialogDescription>Submit your request — admin will review</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Amount (INR)</Label><Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="e.g., 50000" data-testid="loan-amount" /></div>
            <div className="space-y-2"><Label>Repayment Months</Label><Input type="number" value={form.repayment_months} onChange={e => setForm({ ...form, repayment_months: e.target.value })} data-testid="loan-months" /></div>
            <div className="space-y-2"><Label>Reason</Label><Textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Why do you need this loan?" rows={2} data-testid="loan-reason" /></div>
            <Button className="w-full gap-2" onClick={handleApply} disabled={submitting} data-testid="submit-loan-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />} Submit Application
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Loan Configuration</DialogTitle></DialogHeader>
          {editConfig && (
            <div className="space-y-4">
              <div className="space-y-2"><Label>Max Months Salary Advance</Label><Input type="number" value={editConfig.max_months_salary || 3} onChange={e => setEditConfig({ ...editConfig, max_months_salary: parseInt(e.target.value) })} /></div>
              <div className="space-y-2"><Label>Default Repayment Months</Label><Input type="number" value={editConfig.repayment_months || 9} onChange={e => setEditConfig({ ...editConfig, repayment_months: parseInt(e.target.value) })} /></div>
              <div className="space-y-2"><Label>Interest Rate (%)</Label><Input type="number" value={editConfig.interest_rate || 0} onChange={e => setEditConfig({ ...editConfig, interest_rate: parseFloat(e.target.value) })} /></div>
              <Button className="w-full" onClick={handleSaveConfig}>Save Configuration</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoansPage;
